import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { ChannelSessionService } from './channel-session.service';

/**
 * Phase 3a — attaches {@code X-Channel-Id} to outgoing same-origin
 * {@code /api/*} requests when the session carries a resolved channel.
 * Downstream services (Phase 3b's shared filter in ota-security-starter)
 * read the header into TenantScope so per-channel scoping (pricing rules,
 * routing, commission attribution) fires automatically.
 *
 * <p>Skips:
 * <ul>
 *   <li>Non-{@code /api/*} URLs (static assets, third-party fetches, etc.)</li>
 *   <li>The auth-issuance endpoints themselves ({@code /auth/otp/*},
 *       {@code /realms/…/token}) — no valid channel yet to attach, and the
 *       resolver is the AUTHORITY on channel detection at login, not a
 *       consumer of it.</li>
 *   <li>Cross-origin absolute URLs to non-local hosts — external APIs
 *       (Tripall etc.) must never receive our internal channel header.</li>
 *   <li>Requests that already carry an {@code X-Channel-Id} — caller's
 *       explicit override wins, same policy as {@code devTenantInterceptor}
 *       for tenant headers.</li>
 * </ul>
 *
 * <p>No-op when the session has no channel yet — the request goes through
 * unchanged. Legitimate outcome for dev flows where identity-service
 * couldn't resolve a channel from the login request (missing Origin,
 * unknown origin, etc.).
 */
export const channelInterceptor: HttpInterceptorFn = (req, next) => {
  if (!req.url.includes('/api/')) return next(req);
  if (req.headers.has('X-Channel-Id')) return next(req);
  // Skip auth-issuance endpoints — no channel to attach yet, and this is
  // where the resolver runs on the backend side.
  if (isAuthIssuance(req.url)) return next(req);
  // Skip cross-origin third-party APIs (only own backend gets the header).
  if (!isOwnBackendCall(req.url)) return next(req);

  const session = inject(ChannelSessionService, { optional: true });
  const id = session?.channelId();
  if (!id) return next(req);

  return next(req.clone({ setHeaders: { 'X-Channel-Id': id } }));
};

/** True when the request is targeting identity-service's login endpoints
 *  (Keycloak or brokered OTP) — those SET the channel, they don't consume it. */
function isAuthIssuance(url: string): boolean {
  return url.includes('/auth/otp/')
      || url.includes('/protocol/openid-connect/token')
      || url.includes('/auth/password-reset/');
}

/** Same policy as devTenantInterceptor: relative `/api/*` or absolute
 *  localhost URL = own backend; anything else = external. */
function isOwnBackendCall(url: string): boolean {
  if (url.startsWith('/')) return true;
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/)/i.test(url);
}
